/**
 * DetailSection / DetailFields / DetailField — read-only record-detail card.
 *
 * Paired primitives for entity-detail screens (vozidlo, zákazník, leasing).
 * Distinct from the form atoms in `Field.tsx` — those carry focus/error state
 * and a bottom-bordered editable affordance, these just display key/value.
 *
 *   <DetailSection title="Identifikace" badge="№ AT-123">
 *     <DetailFields cols={2}>
 *       <DetailField label="Č. obj." value="AT-123" mono />
 *       <DetailField label="SPZ" value="5AB 1234" mono />
 *     </DetailFields>
 *   </DetailSection>
 *
 * Source: .agents/design/project/artboard-radical-vozidlo-detail.jsx:280-337
 */

import type { ReactNode } from 'react';

interface DetailSectionProps {
  title: ReactNode;
  /** Right-aligned trailing content (badge / counter / link). */
  trailing?: ReactNode;
  span?: 'half' | 'full';
  children: ReactNode;
  className?: string;
}

export function DetailSection({
  title,
  trailing,
  span = 'half',
  children,
  className = '',
}: DetailSectionProps) {
  return (
    <section
      className={[
        'border border-line bg-paper px-[22px] py-5',
        span === 'full' ? 'col-span-full' : '',
        className,
      ].join(' ')}
    >
      <header className="mb-3.5 flex items-center gap-2 font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-ink-3">
        <span aria-hidden className="inline-block h-px w-3.5 bg-accent" />
        <span>{title}</span>
        {trailing && <span className="ml-auto">{trailing}</span>}
      </header>
      {children}
    </section>
  );
}

interface DetailFieldsProps {
  cols?: 1 | 2 | 3;
  children: ReactNode;
  className?: string;
}

const COLS: Record<1 | 2 | 3, string> = {
  1: 'grid-cols-1',
  // Phone (14.8 T2): 2-up survives per the handoff's PhoneVozidloDetail
  // (`vd-two`); 3-up drops to the designed 2-up maximum below `sm`.
  2: 'grid-cols-2',
  3: 'grid-cols-3 max-sm:grid-cols-2',
};

export function DetailFields({ cols = 2, children, className = '' }: DetailFieldsProps) {
  return (
    <div className={`grid ${COLS[cols]} gap-x-[22px] gap-y-3.5 ${className}`}>
      {children}
    </div>
  );
}

interface DetailFieldProps {
  label: ReactNode;
  /** Renders the value text. Pass `null`/`undefined`/empty string to render `emptyText`. */
  value?: ReactNode;
  mono?: boolean;
  /** Mark this field as missing — shown in italic ink-3. */
  empty?: boolean;
  emptyText?: string;
  /** Stretch across the entire field grid. */
  span?: boolean;
  /** Replace the value entirely (e.g. price block, badge composite). Takes precedence over `value`. */
  children?: ReactNode;
  className?: string;
}

export function DetailField({
  label,
  value,
  mono = false,
  empty = false,
  emptyText = '—',
  span = false,
  children,
  className = '',
}: DetailFieldProps) {
  const isEmpty = empty || value == null || value === '';
  return (
    <div
      className={`flex min-w-0 flex-col gap-1 ${span ? 'col-span-full' : ''} ${className}`}
    >
      <span className="font-mono text-[9.5px] font-medium uppercase tracking-[0.08em] text-ink-3">
        {label}
      </span>
      {children ? (
        <span className="text-[13px] font-medium text-ink">{children}</span>
      ) : (
        <span
          className={[
            'text-[13px] font-medium text-ink break-words',
            mono ? 'font-mono text-[12.5px] tracking-[-0.02em]' : '',
            isEmpty ? 'italic font-normal text-ink-3' : '',
          ].join(' ')}
        >
          {isEmpty ? emptyText : value}
        </span>
      )}
    </div>
  );
}
